import type { ApprovalMethod } from "@entrilia/shared/api/workflowTypes";
import type { StateAction } from "@entrilia/shared/types";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import {
  combineValidators,
  invalidResult,
  maxCharactersValidator,
  nonEmptyArrayValidator,
  requiredValidator,
  uniqueValidator,
  validResult,
} from "@entrilia/shared/utilities/validators";
import type { EntityFieldType, Field, ObjectFilter } from "../../../../api/types/objectTypes";
import type {
  ApprovalConfigurationVersion,
  ApprovalRule,
  ApprovalRuleFieldSource,
  ApprovalStep,
  CreateApprovalConfigurationVersionRequest,
} from "../../../../api/types/workflowTypes";
import { getDefaultFilterForFieldType } from "../../../object-filters/objectFilterHelpers";
import { validateObjectFilter } from "../../../object-filters/objectFilterValidator";
import { getApprovalRuleObjectFilter, type ApprovalRuleObjectFilterDefaults } from "./approvalConfigurationHelper";

export interface ApprovalConfigurationState {
  editedStepIndex?: number;
  editedStep?: ApprovalStep;
  editedRuleKeys?: string[];
  isEditedStepDirty?: boolean;
}

export interface StepValidationResults {
  isValid: boolean;
  name: ValidationResult;
  ruleCount: ValidationResult;
  rules: RuleValidationResult[];
}

interface RuleValidationResult {
  groupIds: ValidationResult;
  filter: ValidationResult;
}

export const validateApprovalStep = (
  state: ApprovalConfigurationState,
  storedSteps: ApprovalStep[]
): StepValidationResults | undefined => {
  if (state.editedStep === undefined) {
    return undefined;
  }

  const otherStepNames = storedSteps.reduce<string[]>(
    (result, step, index) => (index === state.editedStepIndex ? result : [...result, step.name]),
    []
  );

  const validateName = combineValidators(
    requiredValidator,
    maxCharactersValidator(100),
    uniqueValidator("A step with this name already exists", otherStepNames)
  );

  const validateRuleCount = nonEmptyArrayValidator("Please add at least one approval rule");

  const validateGroupIds = nonEmptyArrayValidator("Please add at least one user group");

  const nameResult = validateName(state.editedStep.name.trim());

  const ruleCountResult = validateRuleCount(state.editedStep.rules);

  const ruleResults = state.editedStep.rules.map((rule) => {
    const groupIdsResult = validateGroupIds(rule.groupIds);
    const filter = getApprovalRuleObjectFilter(rule);
    if (filter === undefined) {
      return {
        groupIds: groupIdsResult,
        filter: validResult(),
      };
    }

    const dateFilter = filter.dateFilter;
    const filterResult =
      dateFilter?.operator === "Range" && (!dateFilter.from || !dateFilter.to)
        ? invalidResult("Enter a date range")
        : dateFilter?.operator === "Range" && dateFilter.from && dateFilter.to && dateFilter.from > dateFilter.to
          ? invalidResult("Start date must be before end date")
          : validateObjectFilter(filter);

    return {
      groupIds: groupIdsResult,
      filter: filterResult,
    };
  });

  return {
    isValid:
      nameResult.isValid && ruleCountResult.isValid && ruleResults.every((r) => r.groupIds.isValid && r.filter.isValid),
    name: nameResult,
    ruleCount: ruleCountResult,
    rules: ruleResults,
  };
};

export const getCreateApprovalConfigurationVersionRequest = (
  state: ApprovalConfigurationState,
  storedConfiguration: ApprovalConfigurationVersion
): CreateApprovalConfigurationVersionRequest | undefined => {
  if (state.editedStep === undefined) {
    return undefined;
  }

  if (state.editedStepIndex === undefined) {
    return {
      steps: [...storedConfiguration.steps, state.editedStep],
      conversionTargetCurrencyCode: storedConfiguration.conversionTargetCurrencyCode,
      approversAssignmentMode: storedConfiguration.approversAssignmentMode,
    };
  }

  const newSteps = [...storedConfiguration.steps];
  newSteps[state.editedStepIndex] = state.editedStep;

  return {
    steps: newSteps,
    conversionTargetCurrencyCode: storedConfiguration.conversionTargetCurrencyCode,
    approversAssignmentMode: storedConfiguration.approversAssignmentMode,
  };
};

// Actions

export const createNewStepAction = (): StateAction<ApprovalConfigurationState> => (state) => ({
  ...state,
  editedStepIndex: undefined,
  editedStep: {
    name: "",
    rules: [
      {
        groupIds: [],
        method: { requireAll: false, minimumApprovals: 1 },
        fieldScope: "Object",
      },
    ],
  },
  editedRuleKeys: [generateGuid()],
  isEditedStepDirty: false,
});

export const startEditingStepAction =
  (step: ApprovalStep, stepIndex: number): StateAction<ApprovalConfigurationState> =>
  (state) => ({
    ...state,
    editedStepIndex: stepIndex,
    editedStep: { ...step },
    editedRuleKeys: step.rules.map(() => generateGuid()),
    isEditedStepDirty: false,
  });

export const cancelEditingStepAction = (): StateAction<ApprovalConfigurationState> => (state) => ({
  ...state,
  editedStepIndex: undefined,
  editedStep: undefined,
  editedRuleKeys: undefined,
  isEditedStepDirty: undefined,
});

export const setEditedStepNameAction =
  (name: string): StateAction<ApprovalConfigurationState> =>
  (state) => ({
    ...state,
    editedStep: { ...defined(state.editedStep), name },
    isEditedStepDirty: true,
  });

export const removeRuleAction =
  (ruleIndex: number): StateAction<ApprovalConfigurationState> =>
  (state) => ({
    ...state,
    editedStep: {
      ...defined(state.editedStep),
      rules: defined(state.editedStep).rules.filter((_, index) => index !== ruleIndex),
    },
    editedRuleKeys: state.editedRuleKeys?.filter((_, index) => index !== ruleIndex),
    isEditedStepDirty: true,
  });

export const addRuleAction = (): StateAction<ApprovalConfigurationState> => (state) => {
  const newRule: ApprovalRule = {
    groupIds: [],
    method: { requireAll: false, minimumApprovals: 1 },
    fieldScope: "Object",
  };

  return {
    ...state,
    editedStep: {
      ...defined(state.editedStep),
      rules: [...defined(state.editedStep).rules, newRule],
    },
    editedRuleKeys: [...(state.editedRuleKeys ?? []), generateGuid()],
    isEditedStepDirty: true,
  };
};

export const setApprovalMethodAction =
  (ruleIndex: number, method: ApprovalMethod): StateAction<ApprovalConfigurationState> =>
  (state) => ({
    ...state,
    editedStep: {
      ...defined(state.editedStep),
      rules: defined(state.editedStep).rules.map((rule, index) => (index === ruleIndex ? { ...rule, method } : rule)),
    },
    isEditedStepDirty: true,
  });

export const setApprovalRuleGroupIdsAction =
  (ruleIndex: number, groupIds: string[]): StateAction<ApprovalConfigurationState> =>
  (state) => ({
    ...state,
    editedStep: {
      ...defined(state.editedStep),
      rules: defined(state.editedStep).rules.map((rule, index) => (index === ruleIndex ? { ...rule, groupIds } : rule)),
    },
    isEditedStepDirty: true,
  });

const getApprovalRuleFilterForField = (fieldType: EntityFieldType): ApprovalRuleObjectFilterDefaults => ({
  ...clearApprovalRuleFilters,
  ...getDefaultFilterForFieldType(fieldType),
});

const clearApprovalRuleFilters: ApprovalRuleObjectFilterDefaults = {
  numberFilter: undefined,
  textFilter: undefined,
  dateFilter: undefined,
  booleanFilter: undefined,
  multiSelectFilter: undefined,
  guidFilter: undefined,
};

export const setFieldAction =
  (ruleIndex: number, source: ApprovalRuleFieldSource, field: Field): StateAction<ApprovalConfigurationState> =>
  (state) => ({
    ...state,
    editedStep: {
      ...defined(state.editedStep),
      rules: defined(state.editedStep).rules.map((rule, index) =>
        index === ruleIndex
          ? {
              ...rule,
              fieldScope: source.scope,
              relatedObjectType: source.scope === "RelatedObject" ? source.relatedObjectType : undefined,
              fieldId: field.id,
              ...getApprovalRuleFilterForField(field.type),
            }
          : rule
      ),
    },
    isEditedStepDirty: true,
  });

export const removeFilterAction =
  (ruleIndex: number): StateAction<ApprovalConfigurationState> =>
  (state) => ({
    ...state,
    editedStep: {
      ...defined(state.editedStep),
      rules: defined(state.editedStep).rules.map((rule, index) =>
        index === ruleIndex
          ? {
              ...rule,
              fieldScope: "Object",
              relatedObjectType: undefined,
              fieldId: undefined,
              ...clearApprovalRuleFilters,
            }
          : rule
      ),
    },
    isEditedStepDirty: true,
  });

export const setFilterAction =
  (ruleIndex: number, filter: ObjectFilter): StateAction<ApprovalConfigurationState> =>
  (state) => ({
    ...state,
    editedStep: {
      ...defined(state.editedStep),
      rules: defined(state.editedStep).rules.map((rule, index) =>
        index === ruleIndex ? { ...rule, ...clearApprovalRuleFilters, ...filter, fieldId: rule.fieldId } : rule
      ),
    },
    isEditedStepDirty: true,
  });
